/**
 * entityDisplay.ts
 *
 * Single source of truth for how entity data is presented in the UI.
 *
 * Rules:
 *  - Never expose raw UUIDs / internal IDs to users
 *  - Always provide a human-readable fallback (never empty string)
 *  - All display helpers are pure functions – safe to call with null/undefined
 *
 * Usage:
 *   import { entityDisplay } from '@/modules/shared/utils/entityDisplay';
 *
 *   const p = entityDisplay.patient(patient);
 *   // p.label    → patientCode  e.g. "PAT-0001"
 *   // p.subLabel → full name    e.g. "John Smith"
 */

const FALLBACK = '—';
export const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TITLE_ONLY_RE = /^(dr\.?|doctor|prof\.?|professor|mr\.?|mrs\.?|ms\.?|miss)$/i;

// ─── Shape contracts ──────────────────────────────────────────────────────────

export interface PatientDisplayInput {
  patientCode?: string | null;
  fullName?: string | null;
  /** legacy field – still accepted for backward compat */
  patientName?: string | null;
}

export interface HospitalDisplayInput {
  hospitalName?: string | null;
}

export interface HcpDisplayInput {
  hcpName?: string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface CaseDisplayInput {
  caseNumber?: string | null;
  caseCode?: string | null;
  activeCases?: number | null;
  activeCount?: number | null;
  closedCases?: number | null;
  closedCount?: number | null;
  totalCases?: number | null;
}

// ─── Display output shapes ────────────────────────────────────────────────────

export interface EntityLabel {
  /** Primary human-readable identifier – safe to render directly */
  label: string;
}

export interface EntityLabelWithSub extends EntityLabel {
  /** Secondary line – name, count summary, etc. */
  subLabel: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true when a string is null / undefined / empty / whitespace-only */
function blank(v: string | null | undefined): v is null | undefined | '' {
  return !v || v.trim() === '';
}

function safeHumanCode(value: string | null | undefined): string | null {
  if (blank(value)) {
    return null;
  }

  const trimmed = value.trim();
  return GUID_RE.test(trimmed) ? null : trimmed;
}

function safeHumanName(value: string | null | undefined): string | null {
  if (blank(value)) {
    return null;
  }

  const trimmed = value.trim();
  return TITLE_ONLY_RE.test(trimmed) || GUID_RE.test(trimmed) ? null : trimmed;
}

// ─── Entity display functions ─────────────────────────────────────────────────

export const entityDisplay = {
  /**
   * Patient
   *  label    → patientCode (e.g. "PAT-0001")
   *  subLabel → full name   (e.g. "John Smith")
   */
  patient(p: PatientDisplayInput | null | undefined): EntityLabelWithSub {
    const label = safeHumanCode(p?.patientCode) ?? FALLBACK;
    const subLabel = p?.fullName ?? p?.patientName ?? 'Not available';
    return { label, subLabel: blank(subLabel) ? 'Not available' : subLabel! };
  },

  /**
   * Hospital
   *  label → hospital name (e.g. "City General Hospital")
   */
  hospital(h: HospitalDisplayInput | null | undefined): EntityLabel {
    const name = h?.hospitalName?.trim();
    return {
      label: blank(name) || GUID_RE.test(name!) ? 'Not available' : name!,
    };
  },

  /**
   * HCP (Healthcare Professional)
   *  label → resolved name with title-only guard
   */
  hcp(h: HcpDisplayInput | null | undefined): EntityLabel {
    const candidate =
      safeHumanName(h?.hcpName) ??
      safeHumanName(h?.fullName) ??
      safeHumanName([h?.firstName, h?.lastName].filter(Boolean).join(' '));
    return {
      label: blank(candidate) ? 'Not assigned' : candidate!,
    };
  },

  /**
   * Case
   *  label    → caseNumber / caseCode (e.g. "CASE-2025-001")
   *  subLabel → active / closed summary
   */
  case(c: CaseDisplayInput | null | undefined): EntityLabelWithSub {
    const label =
      c?.caseNumber ?? c?.caseCode ?? FALLBACK;
    const active = c?.activeCount ?? c?.activeCases ?? 0;
    const closed = c?.closedCount ?? c?.closedCases ?? 0;
    return {
      label: blank(label) ? FALLBACK : label!,
      subLabel: `${active} Active / ${closed} Closed`,
    };
  },
} as const;
